/* eslint-disable no-undef */
import React from 'react'
import PropTypes from 'prop-types'
import TweetField from '../../../../tweet-input'
import { Field, formValueSelector } from 'redux-form'
import { connect } from 'react-redux'
import CheckboxField from 'components/form/checkbox'
import TagsInput from 'components/form/tagsinput'
import {
  pipe,
  map,
  filter,
  split,
  flatten,
  complement,
  isEmpty,
  defaultTo
} from 'ramda'

const selector = formValueSelector('widget')

export const TweetIdInput = ({
  input: { value, onChange, ...input },
  meta: { error, touched }
}) => (
  <div className={error && touched && 'has-error'}>
    <TagsInput
      inputProps={{
        placeholder: 'Enter Tweet IDs',
        className: 'react-tagsinput-input input-medium',
        ...input,
        onBlur: input.onBlur.bind(input, value)
      }}
      addOnBlur
      value={value}
      onChange={onChange}
    />
  </div>
)

const parseCsv = pipe(
  split('\n'),
  filter(complement(isEmpty)),
  map(split(',')),
  map(row => ({
    tweetId: row[0],
    weight: parseInt(row[1], 10),
    isFinalTweet: row[2] === 'true'
  }))
)

const normalizeCsv = pipe(parseCsv, flatten)

const handleFileChange = (input, file) => {
  const reader = new FileReader()
  reader.onload = e => {
    const value = e.target.result
    input.onChange(normalizeCsv(value))
  }
  reader.readAsText(file)
}

export const TweetIdsFileInput = ({
  input: { value, onChange },
  meta: { error, touched }
}) => {
  return (
    <div>
      <div className={error && touched && 'has-error'}>
        <div className='form-group'>
          <label htmlFor='tweetIdsFileInput'>Upload CSV file:</label>
          <input
            type='file'
            id='tweetIdsFileInput'
            className='form-control-file'
            onChange={e => handleFileChange({ onChange }, e.target.files[0])}
          />
        </div>
        {error && touched && <span className='help-block'>{error}</span>}
      </div>
      {value && value.length > 0 && (
        <div className='form-group'>
          <label htmlFor='tweetIdsFileInput'>Tweet IDs:</label>
          <ul>
            {value.map((tweet, index) => (
              <li key={index}>{tweet.tweetId}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

TweetIdsFileInput.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any
}

TweetIdInput.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any
}

export const normalize = pipe(
  map(split(' ')),
  flatten,
  filter(complement(isEmpty))
)

const SendTweetField = ({
  name,
  isRandomResponse,
  isMosaicConsent = false,
  hasResponseCap,
  sendStartReply
}) => {
  return (
    <div className='row'>
      <div className='col-sm-6'>
        <label>
          <span className='labelRight'>
            Stop timer after:
            <Field
              component='input'
              type='number'
              format={defaultTo(0)}
              name={`${name}.speedThreadExpiration`}
              min={0}
              className='form-control smallField center'
            />
            seconds
          </span>
        </label>
      </div>
      <div className='col-sm-6'>
        <label>
          <span className='labelRight'>
            Retries allowed:
            <Field
              component='input'
              type='number'
              format={defaultTo(0)}
              name={`${name}.maxRetries`}
              min={0}
              className='form-control smallField center'
            />
          </span>
        </label>
        <span className='help-block'>
          Extra attempts a fan gets after their first play. Leave blank or 0 for
          no retries.
        </span>
      </div>
      <div className='br-has-checkbox col-sm-12'>
        <Field
          name={`${name}.sendStartReply`}
          component={CheckboxField}
          label='Send a start reply'
          className='icheckbox_square'
          // Absent/true means "send a reply" (matching the render guard and
          // form-to-widget serialization), so show checked unless explicitly
          // false. Without this the action picker pushes { type } only, and the
          // box would render unchecked while a reply is still built.
          format={value => value !== false}
        />
        {sendStartReply !== false && (
          <div>
            <Field
              name={`${name}.isRandomResponse`}
              component={CheckboxField}
              label='Random Response'
              className='icheckbox_square'
            />
            <Field
              name={`${name}.isMosaicConsent`}
              component={CheckboxField}
              label='Mosaic Consent'
              className='icheckbox_square'
              input={{ value: isMosaicConsent }}
            />
            {!isRandomResponse && (
              <div>
                {' '}
                <TweetField
                  messageName={`${name}.message`}
                  urlName={`${name}.attachmentUrl`}
                  cardUriName={`${name}.cardUri`}
                  isTweet
                  isRandomResponse={`${name}.isRandomResponse`}
                />
              </div>
            )}
            {isRandomResponse && (
              <div>
                <Field
                  name={`${name}.randomResponseTweetIds`}
                  component={TweetIdsFileInput}
                  normalize={defaultTo([])}
                />
                <Field
                  name={`${name}.primary`}
                  component='input'
                  type='hidden'
                  value
                />
                <Field
                  name={`${name}.allowMultiple`}
                  component={CheckboxField}
                  label='Allow Multiple Non Duplicate Responses to the same end user'
                  className='icheckbox_square'
                />
                <Field
                  name={`${name}.hasResponseCap`}
                  component={CheckboxField}
                  label='Send a final Tweet after a specific number of responses'
                  className='icheckbox_square'
                />
                {hasResponseCap && (
                  <Field
                    name={`${name}.responseCap`}
                    component='input'
                    type='number'
                    className='form-control'
                    placeholder='Enter the number of responses'
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

SendTweetField.propTypes = {
  name: PropTypes.string,
  isRandomResponse: PropTypes.bool,
  isMosaicConsent: PropTypes.bool,
  hasResponseCap: PropTypes.bool,
  sendStartReply: PropTypes.bool
}

const StartSpeedThreadWithAutoResponse = connect((state, props) => ({
  isRandomResponse: selector(state, `${props.name}.isRandomResponse`),
  isMosaicConsent: selector(state, `${props.name}.isMosaicConsent`),
  hasResponseCap: selector(state, `${props.name}.hasResponseCap`),
  sendStartReply: selector(state, `${props.name}.sendStartReply`)
}))(SendTweetField)

export default StartSpeedThreadWithAutoResponse



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-triggers-actions/actions/twitter/start-speed-thread/index.js